const countryCache = new Map()

export function getCountryFromTimezone(timezone) {
  if (!timezone) return null

  if (countryCache.has(timezone)) {
    return countryCache.get(timezone)
  }

  const directMappings = {
    'America/New_York': 'United States',
    'America/Los_Angeles': 'United States',
    'America/Chicago': 'United States',
    'Europe/London': 'United Kingdom',
    'Europe/Paris': 'France',
    'Europe/Madrid': 'Spain',
    'Asia/Tokyo': 'Japan',
    'Australia/Sydney': 'Australia',
    'Asia/Dubai': 'UAE'
  }

  if (directMappings?.[timezone]) {
    countryCache.set(timezone, directMappings[timezone])
    return directMappings[timezone]
  }

  const parts = timezone.split('/')

  // Handle single part timezones
  if (parts.length === 1) {
    // Special cases abbreviations
    const specialCases = {
      'US/Alaska': 'United States',
      'US/Aleutian': 'United States',
      'US/Arizona': 'United States',
      'US/Central': 'United States',
      'US/Eastern': 'United States',
      'US/Hawaii': 'United States',
      'US/Mountain': 'United States',
      'US/Pacific': 'United States',
      'Canada/Atlantic': 'Canada',
      'Canada/Central': 'Canada',
      'Canada/Eastern': 'Canada',
      'Canada/Mountain': 'Canada',
      'Canada/Newfoundland': 'Canada',
      'Canada/Pacific': 'Canada',
      'Brazil/East': 'Brazil',
      'Brazil/West': 'Brazil',
      'Mexico/BajaNorte': 'Mexico',
      'Mexico/BajaSur': 'Mexico',
      'Mexico/General': 'Mexico',
      'Chile/Continental': 'Chile',
      'Chile/EasterIsland': 'Chile',
      Cuba: 'Cuba',
      Jamaica: 'Jamaica',
      NZ: 'New Zealand',
      Poland: 'Poland',
      Portugal: 'Portugal',
      Turkey: 'Turkey',
      Egypt: 'Egypt',
      Iran: 'Iran',
      Israel: 'Israel',
      Japan: 'Japan',
      Singapore: 'Singapore',
      ROK: 'South Korea',
      ROC: 'Taiwan',
      PRC: 'China',
      Hongkong: 'Hong Kong',
      GB: 'United Kingdom',
      Eire: 'Ireland'
    }

    const result = specialCases[timezone] || timezone
    countryCache.set(timezone, result)
    return result
  }

  const region = parts[0]
  const city = parts[1]

  // Process lazily loaded region data
  let country = null

  if (region === 'America') {
    country = getAmericanCountry(city)
  } else if (region === 'Europe') {
    country = getEuropeanCountry(city)
  } else if (region === 'Asia') {
    country = getAsianCountry(city)
  } else if (region === 'Africa') {
    country = getAfricanCountry(city)
  } else if (region === 'Australia') {
    country = getAustralianRegion(city)
  } else if (region === 'Pacific') {
    country = getPacificCountry(city)
  } else if (region === 'Atlantic') {
    country = getAtlanticRegion(city)
  } else if (region === 'Indian') {
    country = getIndianOceanRegion(city)
  } else if (region === 'Antarctica') {
    country = 'Antarctica'
  }

  // Region mapping fallback
  if (!country) {
    const regionMappings = {
      Africa: 'Africa',
      America: 'Americas',
      Antarctica: 'Antarctica',
      Asia: 'Asia',
      Atlantic: 'Atlantic',
      Australia: 'Australia',
      Europe: 'Europe',
      Indian: 'Indian Ocean',
      Pacific: 'Pacific'
    }

    country = regionMappings[region] || region
  }

  // Cache the result
  countryCache.set(timezone, country)
  return country
}

// Lazily loaded region data functions
function getAmericanCountry(city) {
  // United States cities
  const usCities = new Set([
    'New_York',
    'Chicago',
    'Los_Angeles',
    'Denver',
    'Phoenix',
    'Anchorage',
    'Detroit',
    'Indianapolis',
    'Louisville',
    'Boise',
    'Juneau',
    'Menominee',
    'Metlakatla',
    'Nome',
    'Sitka',
    'Yakutat',
    'Adak',
    'Honolulu'
  ])

  if (
    usCities.has(city) ||
    city.startsWith('North_Dakota') ||
    city.startsWith('Indiana') ||
    city.startsWith('Kentucky')
  ) {
    return 'United States'
  }

  // Canada cities
  const canadaCities = new Set([
    'Toronto',
    'Vancouver',
    'Montreal',
    'Edmonton',
    'Winnipeg',
    'Halifax',
    'St_Johns',
    'Regina',
    'Whitehorse',
    'Yellowknife',
    'Iqaluit',
    'Moncton',
    'Thunder_Bay',
    'Glace_Bay',
    'Rankin_Inlet',
    'Resolute',
    'Swift_Current'
  ])

  if (canadaCities.has(city)) {
    return 'Canada'
  }

  // Mexico cities
  const mexicoCities = new Set([
    'Mexico_City',
    'Cancun',
    'Monterrey',
    'Chihuahua',
    'Mazatlan',
    'Hermosillo',
    'Tijuana',
    'Merida',
    'Bahia_Banderas',
    'Matamoros',
    'Ojinaga'
  ])

  if (mexicoCities.has(city)) {
    return 'Mexico'
  }

  // Brazil cities
  const brazilCities = new Set([
    'Sao_Paulo',
    'Rio_Branco',
    'Araguaina',
    'Bahia',
    'Belem',
    'Boa_Vista',
    'Campo_Grande',
    'Cuiaba',
    'Eirunepe',
    'Fortaleza',
    'Maceio',
    'Manaus',
    'Noronha',
    'Porto_Velho',
    'Recife',
    'Santarem'
  ])

  if (brazilCities.has(city) || city.startsWith('Brazil')) {
    return 'Brazil'
  }

  // Argentina cities
  const argentinaCities = new Set([
    'Buenos_Aires',
    'Catamarca',
    'Cordoba',
    'Jujuy',
    'Mendoza',
    'Rosario'
  ])

  if (argentinaCities.has(city) || city.startsWith('Argentina')) {
    return 'Argentina'
  }

  // Additional countries in the Americas
  const americanCountries = {
    Asuncion: 'Paraguay',
    La_Paz: 'Bolivia',
    Lima: 'Peru',
    Bogota: 'Colombia',
    Santiago: 'Chile',
    Caracas: 'Venezuela',
    Guayaquil: 'Ecuador',
    Montevideo: 'Uruguay',
    Panama: 'Panama',
    Havana: 'Cuba',
    Port_of_Spain: 'Trinidad and Tobago',
    Santo_Domingo: 'Dominican Republic',
    Guatemala: 'Guatemala',
    El_Salvador: 'El Salvador',
    Tegucigalpa: 'Honduras',
    Managua: 'Nicaragua',
    San_Jose: 'Costa Rica',
    'Port-au-Prince': 'Haiti',
    Jamaica: 'Jamaica',
    Barbados: 'Barbados',
    Nassau: 'Bahamas',
    Belize: 'Belize',
    Cayenne: 'French Guiana',
    Paramaribo: 'Suriname',
    Guyana: 'Guyana',
    Martinique: 'Martinique',
    Grenada: 'Grenada',
    St_Lucia: 'Saint Lucia',
    St_Vincent: 'Saint Vincent and the Grenadines',
    St_Kitts: 'Saint Kitts and Nevis',
    Antigua: 'Antigua and Barbuda',
    Dominica: 'Dominica',
    Grand_Turk: 'Turks and Caicos Islands',
    Cayman: 'Cayman Islands',
    Aruba: 'Aruba',
    Curacao: 'Curaçao',
    Puerto_Rico: 'Puerto Rico'
  }

  return americanCountries[city] || null
}

// Other region functions - will be loaded only when needed
function getEuropeanCountry(city) {
  const europeanCountries = {
    London: 'United Kingdom',
    Paris: 'France',
    Berlin: 'Germany',
    Rome: 'Italy',
    Madrid: 'Spain',
    Moscow: 'Russia',
    Amsterdam: 'Netherlands',
    Brussels: 'Belgium',
    Vienna: 'Austria',
    Zurich: 'Switzerland',
    Dublin: 'Ireland',
    Athens: 'Greece',
    Stockholm: 'Sweden',
    Oslo: 'Norway',
    Helsinki: 'Finland',
    Lisbon: 'Portugal',
    Warsaw: 'Poland',
    Prague: 'Czech Republic',
    Budapest: 'Hungary',
    Kiev: 'Ukraine',
    Kyiv: 'Ukraine',
    Uzhgorod: 'Ukraine',
    Zaporozhye: 'Ukraine',
    Belgrade: 'Serbia',
    Bucharest: 'Romania',
    Sofia: 'Bulgaria',
    Minsk: 'Belarus',
    Copenhagen: 'Denmark',
    Tallinn: 'Estonia',
    Riga: 'Latvia',
    Vilnius: 'Lithuania',
    Ljubljana: 'Slovenia',
    Zagreb: 'Croatia',
    Sarajevo: 'Bosnia and Herzegovina',
    Skopje: 'North Macedonia',
    Tirane: 'Albania',
    Podgorica: 'Montenegro',
    Chisinau: 'Moldova',
    Vaduz: 'Liechtenstein',
    Monaco: 'Monaco',
    San_Marino: 'San Marino',
    Vatican: 'Vatican City',
    Andorra: 'Andorra',
    Luxembourg: 'Luxembourg',
    Malta: 'Malta',
    Volgograd: 'Russia',
    Kaliningrad: 'Russia',
    Samara: 'Russia',
    Astrakhan: 'Russia',
    Saratov: 'Russia',
    Kirov: 'Russia',
    Ulyanovsk: 'Russia',
    Istanbul: 'Turkey',
    Nicosia: 'Cyprus',
    Gibraltar: 'Gibraltar',
    Mariehamn: 'Åland Islands',
    Guernsey: 'Guernsey',
    Jersey: 'Jersey',
    Isle_of_Man: 'Isle of Man',
    Simferopol: 'Crimea'
  }

  return europeanCountries[city] || null
}

function getAsianCountry(city) {
  const asianCountries = {
    Tokyo: 'Japan',
    Shanghai: 'China',
    Seoul: 'South Korea',
    Singapore: 'Singapore',
    Kolkata: 'India',
    Hong_Kong: 'Hong Kong',
    Bangkok: 'Thailand',
    Dubai: 'UAE',
    Jerusalem: 'Israel',
    Riyadh: 'Saudi Arabia',
    Karachi: 'Pakistan',
    Jakarta: 'Indonesia',
    Manila: 'Philippines',
    Kuala_Lumpur: 'Malaysia',
    Taipei: 'Taiwan',
    Almaty: 'Kazakhstan',
    Amman: 'Jordan',
    Baghdad: 'Iraq',
    Baku: 'Azerbaijan',
    Beirut: 'Lebanon',
    Bishkek: 'Kyrgyzstan',
    Brunei: 'Brunei',
    Damascus: 'Syria',
    Dhaka: 'Bangladesh',
    Dili: 'East Timor',
    Dushanbe: 'Tajikistan',
    Gaza: 'Palestine',
    Hebron: 'Palestine',
    Ho_Chi_Minh: 'Vietnam',
    Hovd: 'Mongolia',
    Irkutsk: 'Russia',
    Kabul: 'Afghanistan',
    Kamchatka: 'Russia',
    Kathmandu: 'Nepal',
    Kuwait: 'Kuwait',
    Macau: 'Macau',
    Magadan: 'Russia',
    Muscat: 'Oman',
    Nicosia: 'Cyprus',
    Novosibirsk: 'Russia',
    Omsk: 'Russia',
    Phnom_Penh: 'Cambodia',
    Pyongyang: 'North Korea',
    Qatar: 'Qatar',
    Rangoon: 'Myanmar',
    Yangon: 'Myanmar',
    Sakhalin: 'Russia',
    Samarkand: 'Uzbekistan',
    Tashkent: 'Uzbekistan',
    Tbilisi: 'Georgia',
    Tehran: 'Iran',
    Thimphu: 'Bhutan',
    Ulaanbaatar: 'Mongolia',
    Urumqi: 'China',
    Vladivostok: 'Russia',
    Yakutsk: 'Russia',
    Yekaterinburg: 'Russia',
    Yerevan: 'Armenia',
    Bahrain: 'Bahrain',
    Ashgabat: 'Turkmenistan',
    Aqtau: 'Kazakhstan',
    Aqtobe: 'Kazakhstan',
    Colombo: 'Sri Lanka',
    Vientiane: 'Laos',
    Jayapura: 'Indonesia',
    Makassar: 'Indonesia',
    Pontianak: 'Indonesia',
    Kuching: 'Malaysia'
  }

  return asianCountries[city] || null
}

function getAfricanCountry(city) {
  const africanCountries = {
    Cairo: 'Egypt',
    Johannesburg: 'South Africa',
    Lagos: 'Nigeria',
    Nairobi: 'Kenya',
    Casablanca: 'Morocco',
    Tunis: 'Tunisia',
    Accra: 'Ghana',
    Addis_Ababa: 'Ethiopia',
    Algiers: 'Algeria',
    Asmara: 'Eritrea',
    Bamako: 'Mali',
    Bangui: 'Central African Republic',
    Banjul: 'Gambia',
    Bissau: 'Guinea-Bissau',
    Blantyre: 'Malawi',
    Brazzaville: 'Republic of the Congo',
    Bujumbura: 'Burundi',
    Ceuta: 'Spain (Africa)',
    Conakry: 'Guinea',
    Dakar: 'Senegal',
    Dar_es_Salaam: 'Tanzania',
    Djibouti: 'Djibouti',
    Douala: 'Cameroon',
    El_Aaiun: 'Western Sahara',
    Freetown: 'Sierra Leone',
    Gaborone: 'Botswana',
    Harare: 'Zimbabwe',
    Juba: 'South Sudan',
    Kampala: 'Uganda',
    Khartoum: 'Sudan',
    Kigali: 'Rwanda',
    Kinshasa: 'Democratic Republic of the Congo',
    Libreville: 'Gabon',
    Lome: 'Togo',
    Luanda: 'Angola',
    Lubumbashi: 'Democratic Republic of the Congo',
    Lusaka: 'Zambia',
    Malabo: 'Equatorial Guinea',
    Maputo: 'Mozambique',
    Maseru: 'Lesotho',
    Mbabane: 'Eswatini',
    Mogadishu: 'Somalia',
    Monrovia: 'Liberia',
    Ndjamena: 'Chad',
    Niamey: 'Niger',
    Nouakchott: 'Mauritania',
    Ouagadougou: 'Burkina Faso',
    'Porto-Novo': 'Benin',
    Sao_Tome: 'São Tomé and Príncipe',
    Tripoli: 'Libya',
    Windhoek: 'Namibia'
  }

  return africanCountries[city] || null
}

function getAustralianRegion(city) {
  const australianRegions = {
    Sydney: 'Australia',
    Melbourne: 'Australia',
    Brisbane: 'Australia',
    Perth: 'Australia',
    Adelaide: 'Australia',
    Hobart: 'Australia',
    Darwin: 'Australia',
    Canberra: 'Australia',
    Broken_Hill: 'Australia',
    Lindeman: 'Australia',
    Eucla: 'Australia',
    Currie: 'Australia',
    Lord_Howe: 'Australia'
  }

  return australianRegions[city] || null
}

function getPacificCountry(city) {
  const pacificCountries = {
    Auckland: 'New Zealand',
    Fiji: 'Fiji',
    Guam: 'Guam',
    Honolulu: 'United States (Hawaii)',
    Port_Moresby: 'Papua New Guinea',
    Apia: 'Samoa',
    Chatham: 'New Zealand',
    Tongatapu: 'Tonga',
    Pago_Pago: 'American Samoa',
    Palau: 'Palau',
    Tahiti: 'French Polynesia',
    Marquesas: 'French Polynesia',
    Guadalcanal: 'Solomon Islands',
    Noumea: 'New Caledonia',
    Norfolk: 'Norfolk Island',
    Wake: 'Wake Island',
    Tarawa: 'Kiribati',
    Funafuti: 'Tuvalu',
    Majuro: 'Marshall Islands',
    Nauru: 'Nauru',
    Niue: 'Niue',
    Kosrae: 'Micronesia',
    Pohnpei: 'Micronesia',
    Chuuk: 'Micronesia',
    Saipan: 'Northern Mariana Islands',
    Efate: 'Vanuatu',
    Rarotonga: 'Cook Islands',
    Pitcairn: 'Pitcairn Islands',
    Easter: 'Chile (Easter Island)',
    Galapagos: 'Ecuador (Galápagos)',
    Kiritimati: 'Kiribati',
    Midway: 'United States Minor Outlying Islands',
    Wallis: 'Wallis and Futuna'
  }

  return pacificCountries[city] || null
}

function getAtlanticRegion(city) {
  const atlanticRegions = {
    Azores: 'Portugal',
    Bermuda: 'Bermuda',
    Canary: 'Spain',
    Cape_Verde: 'Cape Verde',
    Faroe: 'Faroe Islands',
    Madeira: 'Portugal',
    Reykjavik: 'Iceland',
    South_Georgia: 'South Georgia and the South Sandwich Islands',
    St_Helena: 'Saint Helena',
    Stanley: 'Falkland Islands'
  }

  return atlanticRegions[city] || null
}

function getIndianOceanRegion(city) {
  const indianOceanRegions = {
    Antananarivo: 'Madagascar',
    Chagos: 'British Indian Ocean Territory',
    Christmas: 'Christmas Island',
    Cocos: 'Cocos (Keeling) Islands',
    Comoro: 'Comoros',
    Kerguelen: 'French Southern Territories',
    Mahe: 'Seychelles',
    Maldives: 'Maldives',
    Mauritius: 'Mauritius',
    Mayotte: 'Mayotte',
    Reunion: 'Réunion'
  }

  return indianOceanRegions[city] || null
}

const countryToTimezonesCache = new Map()

export async function getTimezonesFromCountry(country) {
  if (!country) {
    return []
  }

  if (countryToTimezonesCache.has(country)) {
    return countryToTimezonesCache.get(country)
  }

  let timezones
  try {
    const module = await import('@/Util/timezones.json')
    timezones = module.default
  } catch (error) {
    return []
  }

  if (!Array.isArray(timezones)) {
    return []
  }

  const result = timezones.filter(timezone => {
    const timezoneCountry = getCountryFromTimezone(timezone)
    return timezoneCountry === country
  })

  countryToTimezonesCache.set(country, result)
  return result
}
